/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
// ============================================================
// HEADER SECTION - Enhanced with smooth animations
// ============================================================

import { buildImageUrl, publicStoreText } from "../../theme1/utils/theme1Helpers";
import useFixedHeader from "../../hooks/useFixedHeader";

export default function HeaderSection({
  storeData,
  theme,
  selectedTheme,
  setSelectedTheme,
  isLoggedIn,
  customer,
  showAccountMenu,
  setShowAccountMenu,
  loadCustomerOrders,
  setShowOrdersModal,
  handleLogout,
  setShowAuthModal,
  cartItems,
  setCartOpen,
}) {
  // ====== Calculate Total Quantity ======
  const headerSubtitle = publicStoreText(storeData.storeSlogan, storeData.description, storeData.sectorType, storeData.SectorType);
  const totalQty = cartItems.reduce((s, i) => s + (i.quantity || i.qty || 0), 0);
  const { headerRef, height } = useFixedHeader();

  return (
    <>
    <header
      ref={headerRef}
      className="fixed top-0 left-0 right-0 z-30 backdrop-blur-md border-b transition-all duration-300"
      style={{
        backgroundColor: "rgba(0,0,0,0.85)",
        borderColor: "rgba(255,255,255,0.1)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        width: "100%",
        zIndex: 1000
      }}
    >
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">

        {/* ====== Logo & Store Name ====== */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
          <div
            className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center flex-shrink-0 transform transition-all duration-300 hover:scale-110 hover:rotate-6"
          >
            {storeData.logo ? (
              <img
                src={buildImageUrl(storeData.logo)}
                alt={storeData.storeName || "Logo"}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <span className="text-xl sm:text-2xl" style={{ color: theme.primary }}>
                🏪
              </span>
            )}
          </div>

          <div className="leading-tight text-right min-w-0 flex-1">
            <h1
              className="text-base sm:text-lg md:text-xl font-bold tracking-wide truncate transition-all duration-300 hover:text-opacity-80"
              style={{ color: "white" }}
            >
              {storeData.storeName || storeData.ClientName || "متجرنا"}
            </h1>
            {headerSubtitle && (
              <p className="text-[10px] sm:text-xs text-white/60 hidden sm:block transition-all duration-300 truncate">
                {headerSubtitle}
              </p>
            )}
          </div>
        </div>

        {/* ====== Actions (Login/Account & Cart) ====== */}
        <div className="flex items-center gap-2">

          {/* ====== User Account / Login Button ====== */}
          {isLoggedIn ? (
            <div className="relative">
              <button
                onClick={() => setShowAccountMenu(v => !v)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-xs text-white border border-white/20 bg-white/10 hover:bg-white/20 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                style={{
                  boxShadow: showAccountMenu ? `0 0 20px ${theme.primary}40` : "none"
                }}
              >
                <span className="w-6 h-6 rounded-full bg-gradient-to-br from-white/30 to-white/10 flex items-center justify-center transition-transform duration-300 hover:rotate-12">
                  👤
                </span>
                <span className="hidden sm:inline">{customer?.name || "حسابي"}</span>
              </button>

              {/* ====== Account Dropdown Menu ====== */}
              {showAccountMenu && (
                <div
                  className="absolute top-12 left-0 bg-white text-black rounded-xl shadow-2xl p-2 w-44 animate-in fade-in slide-in-from-top-2 duration-200"
                  style={{
                    boxShadow: "0 10px 40px rgba(0,0,0,0.3)"
                  }}
                >
                  <button
                    onClick={() => {
                      loadCustomerOrders();
                      setShowOrdersModal(true);
                      setShowAccountMenu(false);
                    }}
                    className="block w-full text-right px-3 py-2 text-sm hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 rounded-lg transition-all duration-200 transform hover:translate-x-1"
                  >
                    📦 طلباتي
                  </button>

                  <button
                    onClick={() => {
                      handleLogout();
                      setShowAccountMenu(false);
                    }}
                    className="block w-full text-right px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 transform hover:translate-x-1"
                  >
                    🚪 تسجيل الخروج
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => setShowAuthModal(true)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-xs text-white border border-white/20 bg-white/10 hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
            >
              <span className="w-6 h-6 rounded-full bg-gradient-to-br from-white/30 to-white/10 flex items-center justify-center">
                👤
              </span>
              <span className="hidden sm:inline">تسجيل الدخول</span>
            </button>
          )}

          {/* ====== Cart Button ====== */}
          <button
            onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-xs text-white transition-all duration-300 transform hover:scale-110 hover:shadow-2xl"
            style={{
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
              boxShadow: `0 6px 20px ${theme.primary}55`
            }}
          >
            <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center animate-pulse">
              🛒
            </span>
            <span className="hidden sm:inline font-bold">السلة</span>

            {/* ====== Cart Badge ====== */}
            {totalQty > 0 && (
              <span
                className="absolute -top-2 -left-2 bg-yellow-400 text-black text-xs rounded-full px-1.5 min-w-[20px] h-5 flex items-center justify-center shadow-lg font-bold animate-bounce"
                style={{
                  boxShadow: "0 4px 12px rgba(250, 204, 21, 0.5)"
                }}
              >
                {totalQty}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
    <div aria-hidden="true" style={{ height }} />
    </>
  );
}
